import React, { useCallback, useRef, useState } from 'react'
import * as Yup from 'yup'
import { equals } from 'ramda'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { useDispatch } from 'react-redux'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { IconButton, Typography } from '@material-ui/core'

import effects from 'app/effects/effects'

import { fileToBase64 } from 'libs/file'
import { SECONDARY } from 'app/constants/button'
import { messageAlertError } from 'commons/error'
import { AVAILABLE } from 'app/constants/statusColors'

import PanelsInfo from 'app/componentV4/Panels/Info'
import MessageAlert from 'app/components/MessageAlert'
import LabelError from 'app/componentV4/Labels/LabelError'
import UploadImage from 'app/componentV4/Input/UploadImage'
import TypeWriterInput from 'app/componentV4/Input/TypeWriterInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import IllustrationOnboard from 'app/componentV4/Panels/IllustrationOnboard'

const Step2 = ({ initial, goTo, onChange, onSubmit, isLoading }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const formikRef = useRef(null)
  const [isEdit, setIsEdit] = useState(false)
  const [isCurrentlyTyping, setIsCurrentlyTyping] = useState(initial.typing)

  const uploadAvatar = useMutation({
    mutationFn: async (fileBase64) => {
      const response = await effects.apis.uploadProfilePhoto({
        data: { file: fileBase64 },
      })
      return response
    },
    onSuccess: (response) => {
      if (formikRef.current)
        formikRef.current.setFieldValue('logo', response.url)
    },
    onError: (error) => {
      const msg = error.response?.data
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(msg, formatMessage),
        }),
      )
    },
  })

  const handleUploadAvatar = async (file) => {
    const base64 = await fileToBase64(file)
    uploadAvatar.mutate(base64)
  }

  const handleStepBack = useCallback(() => {
    onChange(formikRef.current.values)
    goTo(1)
  }, [onChange, goTo])

  return (
    <IllustrationOnboard>
      <div className="overflow-auto">
        <Typography className="font-500 text-16 text-primary">
          <IconButton onClick={() => handleStepBack()}>
            <ArrowBackIcon />
          </IconButton>
          <FormattedMessage
            id="step2.title"
            defaultMessage="Fill Profile Information"
          />
        </Typography>

        <PanelsInfo
          type={AVAILABLE}
          className="my-10 flex space-x-3 items-center"
        >
          <Typography className="text-14">
            <FormattedMessage
              id="step2.info"
              defaultMessage="You can update this profile information anytime on the profile page." // eslint-disable-line max-len
            />
          </Typography>
        </PanelsInfo>

        <Formik
          innerRef={formikRef}
          validateOnMount
          initialTouched={{ logo: true }}
          initialValues={{
            logo: initial.logo || '',
            description: initial.description,
          }}
          validationSchema={Yup.object({
            logo: Yup.string().required('Logo is required.'),
            description: Yup.string()
              .min(100, 'Minimum of 100 characters is required.')
              .max(400)
              .required(
                formatMessage({
                  id: 'composeDialog.field.required',
                }),
              ),
          })}
          onSubmit={(values) => {
            onSubmit(values)
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            isValid,
          }) => (
            <Form>
              <div className="justify-center space-y-10">
                <div className="space-y-5">
                  <div className="flex">
                    <div className="justify-center flex md:mr-5">
                      <UploadImage
                        name="logo"
                        src={values.logo}
                        onChange={handleUploadAvatar}
                        isLoading={uploadAvatar.isPending}
                        error={Boolean(errors.logo)}
                      />
                    </div>
                    <div className="ml-5 break-all">
                      <Typography className="text-16 text-primary font-bold">
                        {initial.placeName}
                      </Typography>
                      <Typography className="text-14 text-primary">
                        {initial.category.label}
                      </Typography>
                    </div>
                  </div>
                  {Boolean(errors.logo) && (
                    <LabelError
                      id={'onboard-upload-logog'}
                      text={errors.logo}
                    />
                  )}
                </div>
                <div>
                  {(isCurrentlyTyping || isEdit) && (
                    <Typography className="text-14 text-primary my-3">
                      <FormattedMessage
                        id="step2.generated"
                        defaultMessage="* We created this for you"
                      />
                    </Typography>
                  )}
                  <TypeWriterInput
                    id="on-board-place-description"
                    placeholder={
                      'Enter description (Minimum 100 characters required *)'
                    }
                    // {formatMessage({
                    //   id: 'profile.description.label',
                    // })}
                    fullText={values.description}
                    typing={isCurrentlyTyping}
                    charDelay={40}
                    maxLength={400}
                    inputProps={{
                      maxLength: 400,
                      'aria-label': 'On board step2 Typing',
                    }}
                    multiline
                    rows={7}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    onComplete={(value, typing) => {
                      setFieldValue('description', value)
                      setIsCurrentlyTyping(typing)
                      setIsEdit(equals(value, initial.originAI))
                      setFieldTouched('description', true, true)
                    }}
                  />
                </div>

                <ContainedButton
                  id="on-board-step2-submit"
                  type="submit"
                  color={SECONDARY}
                  className="w-full h-48"
                  disabled={isCurrentlyTyping || isLoading || !isValid}
                  isLoading={isLoading}
                >
                  <FormattedMessage
                    id="step2.submit"
                    defaultMessage="Submit for Review"
                  />
                </ContainedButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </IllustrationOnboard>
  )
}

Step2.propTypes = {
  initial: PropTypes.shape({
    logo: PropTypes.object,
    typing: PropTypes.bool,
    address: PropTypes.string,
    category: PropTypes.object,
    location: PropTypes.object,
    placeName: PropTypes.string,
    originAI: PropTypes.object,
    googlePlaceId: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  goTo: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
}

export default Step2
